'use client'

import useEmblaCarousel from 'embla-carousel-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Chevron } from '../../icons'
import { IconButton } from '../IconButton/IconButton'
import { cn, warnIfEmptyLabel } from '../../utils'
import type { EmblaOptionsType } from 'embla-carousel'
import type { EmblaViewportRefType } from 'embla-carousel-react'

type CarouselContextType = {
  embla: ReturnType<typeof useEmblaCarousel>[1] | null
  emblaRef: EmblaViewportRefType
  scrollPrev: () => void
  scrollNext: () => void
  selectedIndex: number
  scrollTo: (index: number) => void
  canScrollPrev: boolean
  canScrollNext: boolean
}

const CarouselContext = createContext<CarouselContextType | null>(null)

function useCarouselContext() {
  const context = useContext(CarouselContext)
  if (!context)
    throw new Error('Carousel subcomponents must be used within <Carousel>')
  return context
}

export function Carousel({
  options,
  children,
  className,
  ...rest
}: {
  options?: EmblaOptionsType
  children: React.ReactNode
  className?: string
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const updateStates = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', updateStates)
    emblaApi.on('reInit', updateStates)

    updateStates()

    return () => {
      emblaApi.off('select', updateStates)
      emblaApi.off('reInit', updateStates)
    }
  }, [emblaApi, updateStates])

  return (
    <CarouselContext.Provider
      value={{
        embla: emblaApi ?? null,
        emblaRef,
        scrollPrev,
        scrollNext,
        selectedIndex,
        scrollTo,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        className={cn('flex flex-wrap items-center gap-4', className)}
        {...rest}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

export function CarouselContent({
  viewportClassName,
  containerClassName,
  gap = 'gap-4',
  children,
  ...rest
}: {
  viewportClassName?: string
  containerClassName?: string
  gap?: string
  children: React.ReactNode
}) {
  const { emblaRef } = useCarouselContext()

  return (
    <div
      className={cn('flex-1 shrink grow overflow-hidden', viewportClassName)}
      ref={emblaRef}
      {...rest}
    >
      <div className={cn('flex', gap, containerClassName)}>{children}</div>
    </div>
  )
}

export function CarouselItem({
  className,
  children,
  ...rest
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('shrink-0', className)} {...rest}>
      {children}
    </div>
  )
}

export function CarouselPrevButton({
  children,
  className,
  ariaLabel,
  ...rest
}: {
  children?: React.ReactNode
  className?: string
  /**
   * Accessible name for the previous button. Required — provide a localized
   * string from the consuming app.
   */
  ariaLabel: string
}) {
  const { scrollPrev, canScrollPrev } = useCarouselContext()
  warnIfEmptyLabel(ariaLabel, 'CarouselPrevButton: ariaLabel')
  return (
    <IconButton
      onClick={scrollPrev}
      size="md"
      disabled={!canScrollPrev}
      className={cn('grow-0', className)}
      aria-label={ariaLabel}
      {...rest}
    >
      {children ?? (
        <Chevron className="size-2.5! mr-0.5 -rotate-90" aria-hidden="true" />
      )}
    </IconButton>
  )
}

export function CarouselNextButton({
  children,
  className,
  ariaLabel,
  ...rest
}: {
  children?: React.ReactNode
  className?: string
  /**
   * Accessible name for the next button. Required — provide a localized
   * string from the consuming app.
   */
  ariaLabel: string
}) {
  const { scrollNext, canScrollNext } = useCarouselContext()
  warnIfEmptyLabel(ariaLabel, 'CarouselNextButton: ariaLabel')
  return (
    <IconButton
      onClick={scrollNext}
      size="md"
      disabled={!canScrollNext}
      className={cn('grow-0', className)}
      aria-label={ariaLabel}
      {...rest}
    >
      {children ?? (
        <Chevron className="size-2.5! ml-0.5 rotate-90" aria-hidden="true" />
      )}
    </IconButton>
  )
}

export function CarouselDots({
  className,
  formatDotAriaLabel,
  ...rest
}: {
  className?: string
  /**
   * Builds the accessible name for each dot. Receives the zero-based slide
   * index; the consuming app formats and localizes the string
   * (e.g. `(i) => t('carousel.goToSlide', { n: i + 1 })`). Required.
   */
  formatDotAriaLabel: (index: number) => string
}) {
  const { embla, selectedIndex, scrollTo } = useCarouselContext()
  const [scrollSnaps, setScrollSnaps] = useState<Array<number>>([])

  useEffect(() => {
    if (!embla) return
    setScrollSnaps(embla.scrollSnapList())
  }, [embla])

  return (
    <div
      className={cn('flex-12/12 flex justify-center gap-2', className)}
      {...rest}
    >
      {scrollSnaps.map((_, index) => {
        const label = formatDotAriaLabel(index)
        warnIfEmptyLabel(label, 'CarouselDots: formatDotAriaLabel')

        return (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 w-2 rounded-full transition',
              index === selectedIndex ? 'bg-black' : 'bg-gray-300',
            )}
            aria-label={label}
            aria-current={index === selectedIndex ? 'true' : 'false'}
          />
        )
      })}
    </div>
  )
}
